// Layer 2 (Engine): effectiveness by re-watching the watched signal after an action.
// See docs/engines/effectiveness.md.

import type { Case, EffectivenessRead, RecommendedAction } from '@/versions/v1/schema/case';
import type { ControlLimitSet, ExcursionEvent } from '@/versions/v1/schema/spc';
import type { SignalRole } from '@/versions/v1/schema/tag-mapping';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { applyRecommendedActions, type AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import {
  ADVISORY_RULES,
  VARIATION_EVENTS,
} from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { DIAGNOSTIC_RULES } from '@/versions/v1/shared/bunker-hill-seed';
import { MONITORED_VARIABLES, TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';
import { asCaseTime, formatClockShort } from '@/versions/v1/shared/clock.derive';
import type { Diagnosis } from '@/versions/v1/shared/DiagnosisEngine';
import { formatValue, readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { excursions, spcRead } from '@/versions/v1/shared/SpcEngine';
import type { DiagnosticRule } from '@/versions/v1/schema/diagnostic-rule';

const RULES: readonly DiagnosticRule[] = [...DIAGNOSTIC_RULES, ...ADVISORY_RULES];
const RULE_BY_ID = new Map(RULES.map((row) => [row.id, row]));
const MAPPING_BY_ROLE = new Map(TAG_MAPPING.map((row) => [row.role, row]));
const VARIABLE_BY_ID = new Map(MONITORED_VARIABLES.map((row) => [row.id, row]));

const EXCURSION_RULE_SUFFIXES = [
  'six-point-trend',
  'beyond-limit',
  'widening-variability',
  'cyclical',
] as const;

const SUBPROCESS_DEFAULT_SIGNAL: Record<string, SignalRole> = {
  'sp-zn': 'zn-recovery',
  'sp-pb': 'ag-con-grade',
  'sp-grind': 'grind-p80',
};

/** Hourly in-process roles per subprocess — preferred over daily KPI when the rule has no `when`. */
const SUBPROCESS_HOURLY_SIGNAL: Record<string, SignalRole> = {
  'sp-zn': 'zn-conditioner-ph',
  'sp-pb': 'pb-rougher-ph',
  'sp-grind': 'mill-power-ratio',
};

const HOURLY_SERIES = new Set([
  'rs-wit-021',
  'rs-jt-023',
  'rs-cyc-p',
  'rs-mill-dens',
  'rs-pb-ph',
  'rs-zn-ph',
]);

function variableIdForRole(role: SignalRole): string | undefined {
  const map = MAPPING_BY_ROLE.get(role);
  if (!map?.seriesId) return undefined;
  return `mv-${map.seriesId.replace(/^rs-/, '')}`;
}

function variableIdFromExcursionEventId(eventId: string): string | undefined {
  if (!eventId.startsWith('ex-')) return undefined;
  const rest = eventId.slice(3);
  for (const rule of EXCURSION_RULE_SUFFIXES) {
    const suffix = `-${rule}`;
    if (rest.endsWith(suffix)) return rest.slice(0, -suffix.length);
  }
  return undefined;
}

function signalRoleForSeededEvent(variationEventId: string): SignalRole | null {
  const event = VARIATION_EVENTS.find((row) => row.id === variationEventId);
  if (!event) return null;
  const ruleId = event.matchedRules[0]?.ruleId;
  const rule = ruleId ? RULE_BY_ID.get(ruleId) : undefined;
  if (rule?.when?.[0]?.signal) return rule.when[0].signal;
  const hourly = SUBPROCESS_HOURLY_SIGNAL[event.subprocessId];
  if (hourly) return hourly;
  return SUBPROCESS_DEFAULT_SIGNAL[event.subprocessId] ?? null;
}

function isHourlyVariable(variableId: string): boolean {
  const variable = VARIABLE_BY_ID.get(variableId);
  return variable ? HOURLY_SERIES.has(variable.seriesId) : false;
}

function watchedSignalForCase(
  caseItem: Case,
  diagnosis: Diagnosis,
): { signalRole: SignalRole | null; variableId: string | null } {
  if (caseItem.id.startsWith('case-mv-')) {
    const variableId = caseItem.id.replace(/^case-/, '');
    const candidate = diagnosis.candidates.find((row) => row.excursion.variableId === variableId);
    const signalRole =
      candidate?.rule.when?.[0]?.signal ??
      TAG_MAPPING.find((row) => row.seriesId === `rs-${variableId.replace(/^mv-/, '')}`)?.role ??
      null;
    return { signalRole, variableId };
  }

  if (caseItem.variationEventId?.startsWith('ex-')) {
    const variableId = variableIdFromExcursionEventId(caseItem.variationEventId);
    const candidate = diagnosis.candidates.find((row) => row.excursion.id === caseItem.variationEventId);
    const signalRole =
      candidate?.rule.when?.[0]?.signal ??
      (variableId && isHourlyVariable(variableId)
        ? TAG_MAPPING.find((row) => row.seriesId === `rs-${variableId.replace(/^mv-/, '')}`)?.role ?? null
        : variableId
          ? SUBPROCESS_HOURLY_SIGNAL[
              TAG_MAPPING.find((row) => row.seriesId === `rs-${variableId.replace(/^mv-/, '')}`)
                ?.subprocessId ?? ''
            ] ?? null
          : null) ??
      (variableId
        ? TAG_MAPPING.find((row) => row.seriesId === `rs-${variableId.replace(/^mv-/, '')}`)?.role ?? null
        : null);
    return { signalRole, variableId: variableId ?? null };
  }

  if (caseItem.variationEventId) {
    const seeded = VARIATION_EVENTS.find((row) => row.id === caseItem.variationEventId);
    if (seeded?.variableId) {
      const variable = VARIABLE_BY_ID.get(seeded.variableId);
      const signalRole =
        TAG_MAPPING.find((row) => row.seriesId === variable?.seriesId)?.role ??
        signalRoleForSeededEvent(caseItem.variationEventId);
      return { signalRole, variableId: seeded.variableId };
    }
    const signalRole = signalRoleForSeededEvent(caseItem.variationEventId);
    return { signalRole, variableId: signalRole ? variableIdForRole(signalRole) ?? null : null };
  }

  return { signalRole: null, variableId: null };
}

/** Action the effectiveness read watches — latest session stamp, else first proposed, else seed-approved. */
export function watchedActionForCase(
  caseItem: Case,
  actions: readonly RecommendedAction[],
  advisory: AdvisorySessionState,
): RecommendedAction | undefined {
  const merged = applyRecommendedActions(actions, advisory);
  const caseActions = merged.filter((row) => row.caseId === caseItem.id);

  let latestStamp: SeriesTime | undefined;
  let latestAction: RecommendedAction | undefined;
  for (const action of caseActions) {
    const sessionStamp = advisory.actionDecisions.get(action.id)?.at;
    if (sessionStamp && (!latestStamp || sessionStamp > latestStamp)) {
      latestStamp = sessionStamp;
      latestAction = action;
    }
  }
  if (latestAction) return latestAction;

  const seedApproved = caseActions.find(
    (row) =>
      (row.status === 'approved' || row.status === 'done') &&
      (row.decision?.at || advisory.actionDecisions.has(row.id)),
  );
  if (seedApproved) return seedApproved;

  const proposed = caseActions.find((row) => row.status === 'proposed');
  if (proposed) return proposed;

  return (
    caseActions.find((row) => row.status === 'approved' || row.status === 'done') ?? caseActions[0]
  );
}

function actionStampForCase(
  caseItem: Case,
  actions: readonly RecommendedAction[],
  _state: SessionState,
  advisory: AdvisorySessionState,
): SeriesTime | undefined {
  const watched = watchedActionForCase(caseItem, actions, advisory);
  if (!watched) return undefined;

  if (watched.status === 'done' && watched.implementedAt) {
    return asCaseTime(watched.implementedAt);
  }
  const sessionStamp = advisory.actionDecisions.get(watched.id)?.at;
  if (sessionStamp) return sessionStamp;

  const seedStamp = watched.decision?.at;
  if ((watched.status === 'approved' || watched.status === 'done') && seedStamp) {
    return asCaseTime(seedStamp);
  }
  return undefined;
}

function excursionAt(variableId: string, state: SessionState): ExcursionEvent | undefined {
  return excursions(state).find((row) => row.variableId === variableId);
}

function trendLabel(trend: 'rising' | 'falling' | 'stable'): string {
  if (trend === 'rising') return 'rising';
  if (trend === 'falling') return 'falling';
  return 'stable';
}

function formatMetric(variableId: string, value: number): string {
  const variable = VARIABLE_BY_ID.get(variableId);
  const reading = variable ? readingByIdLookup(variable.readingId) : undefined;
  if (!reading) return value.toFixed(1);
  return formatValue(reading, { value, withUnit: false });
}

/** The two control limits the stable-trend rule needs (`spcRead(...).limits`). */
type LimitBounds = Pick<ControlLimitSet, 'lcl' | 'ucl'>;

/**
 * A stable post-action trend still counts as worsening when the latest point
 * sits beyond the control limit in the excursion's direction AND has moved
 * that way from the first post-action point (komatsu.F17 #754). The engine's
 * own sentence already detects the case; the state must agree with it.
 */
function stableBeyondLimit(
  excursionDirection: 'up' | 'down',
  first: number | undefined,
  latest: number | undefined,
  limits: LimitBounds | undefined,
): boolean {
  if (first === undefined || latest === undefined || !limits) return false;
  if (excursionDirection === 'down') {
    return latest < limits.lcl && latest < first;
  }
  return latest > limits.ucl && latest > first;
}

/** Exported for the effectiveness engine probe (`scripts/seed/effectiveness-probe-entry.ts`). */
export function compareTrend(
  postTrend: 'rising' | 'falling' | 'stable',
  excursionDirection: 'up' | 'down' | undefined,
  first: number | undefined,
  latest: number | undefined,
  limits: LimitBounds | undefined,
): EffectivenessRead['state'] {
  if (!excursionDirection) return 'no-change';
  const excursionTrend = excursionDirection === 'up' ? 'rising' : 'falling';
  if (postTrend === 'stable') {
    return stableBeyondLimit(excursionDirection, first, latest, limits) ? 'worsening' : 'no-change';
  }
  if (postTrend !== excursionTrend) return 'improving';
  return 'worsening';
}

function actionStampForAction(
  action: RecommendedAction,
  _state: SessionState,
  advisory: AdvisorySessionState,
): SeriesTime | undefined {
  if (action.status === 'done' && action.implementedAt) {
    return asCaseTime(action.implementedAt);
  }
  const sessionStamp = advisory.actionDecisions.get(action.id)?.at;
  if (sessionStamp) return sessionStamp;
  const seedStamp = action.decision?.at;
  if ((action.status === 'approved' || action.status === 'done') && seedStamp) {
    return asCaseTime(seedStamp);
  }
  return undefined;
}

function effectivenessReadForStamp(
  caseItem: Case,
  variableId: string,
  signalRole: SignalRole,
  actionAt: SeriesTime,
  state: SessionState,
): EffectivenessRead {
  const variable = VARIABLE_BY_ID.get(variableId);
  const reading = variable ? readingByIdLookup(variable.readingId) : undefined;
  const label = reading?.label ?? variableId;

  let read;
  try {
    read = spcRead(variableId, state);
  } catch {
    return {
      caseId: caseItem.id,
      signalRole,
      variableId,
      label,
      state: 'unobservable',
      sentence: `${label} — series unavailable`,
    };
  }

  const pointsSinceAction = read.points.filter((p) => p.at >= actionAt);
  const since = formatClockShort(actionAt);

  if (pointsSinceAction.length < 3) {
    const latest = pointsSinceAction[pointsSinceAction.length - 1]?.value;
    const first = pointsSinceAction[0]?.value;
    return {
      caseId: caseItem.id,
      signalRole,
      variableId,
      label,
      state: 'watching',
      actionAt,
      since,
      pointsSince: pointsSinceAction.length,
      before: first !== undefined ? { value: first, trend: read.trend } : undefined,
      after: latest !== undefined ? { value: latest, trend: read.trend } : undefined,
      sentence: `${label} ${first !== undefined && latest !== undefined ? `${formatMetric(variableId, first)} → ${formatMetric(variableId, latest)}, ` : ''}watching since ${since.split('·').pop()?.trim() ?? since} · ${pointsSinceAction.length} point${pointsSinceAction.length === 1 ? '' : 's'}`,
    };
  }

  const hadExcursionAtAction = Boolean(excursionAt(variableId, { ...state, cursor: actionAt }));
  const hasExcursionNow = Boolean(excursionAt(variableId, state));
  if (hadExcursionAtAction && !hasExcursionNow) {
    const first = pointsSinceAction[0]?.value;
    const latest = pointsSinceAction[pointsSinceAction.length - 1]?.value;
    return {
      caseId: caseItem.id,
      signalRole,
      variableId,
      label,
      state: 'resolved',
      actionAt,
      since,
      pointsSince: pointsSinceAction.length,
      before: first !== undefined ? { value: first, trend: read.trend } : undefined,
      after: latest !== undefined ? { value: latest, trend: read.trend } : undefined,
      sentence: `${label} ${first !== undefined && latest !== undefined ? `${formatMetric(variableId, first)} → ${formatMetric(variableId, latest)}, ` : ''}resolved · ${pointsSinceAction.length} points since action`,
    };
  }

  const excursion = excursionAt(variableId, state) ?? excursionAt(variableId, { ...state, cursor: actionAt });
  const first = pointsSinceAction[0]?.value;
  const latest = pointsSinceAction[pointsSinceAction.length - 1]?.value;
  const postTrend = read.trend;
  const effectivenessState = compareTrend(postTrend, excursion?.direction, first, latest, read.limits);
  const sentenceDate = since.split('·').pop()?.trim() ?? since;
  const trendWord =
    postTrend === 'stable' && effectivenessState === 'worsening'
      ? `no improvement since ${sentenceDate}`
      : `${trendLabel(postTrend)} since ${sentenceDate}`;

  return {
    caseId: caseItem.id,
    signalRole,
    variableId,
    label,
    state: effectivenessState,
    actionAt,
    since,
    pointsSince: pointsSinceAction.length,
    before: first !== undefined ? { value: first, trend: postTrend } : undefined,
    after: latest !== undefined ? { value: latest, trend: postTrend } : undefined,
    sentence: `${label} ${first !== undefined && latest !== undefined ? `${formatMetric(variableId, first)} → ${formatMetric(variableId, latest)}, ` : ''}${trendWord} · ${pointsSinceAction.length} point${pointsSinceAction.length === 1 ? '' : 's'}`,
  };
}

export function effectivenessForAction(
  action: RecommendedAction,
  caseItem: Case,
  state: SessionState,
  advisory: AdvisorySessionState,
  diagnosis: Diagnosis,
  actions: readonly RecommendedAction[],
): EffectivenessRead | undefined {
  const merged = applyRecommendedActions(actions, advisory);
  const resolved = merged.find((row) => row.id === action.id);
  if (!resolved || (resolved.status !== 'approved' && resolved.status !== 'done')) return undefined;

  const { signalRole, variableId } = watchedSignalForCase(caseItem, diagnosis);
  if (!variableId || !signalRole) {
    return {
      caseId: caseItem.id,
      signalRole: null,
      variableId: null,
      label: '—',
      state: 'unobservable',
      sentence: 'No watched signal for this case',
    };
  }

  const actionAt = actionStampForAction(resolved, state, advisory);
  if (!actionAt) {
    return {
      caseId: caseItem.id,
      signalRole,
      variableId,
      label: VARIABLE_BY_ID.get(variableId)
        ? readingByIdLookup(VARIABLE_BY_ID.get(variableId)!.readingId)?.label ?? variableId
        : variableId,
      state: 'awaiting-action',
      sentence: '— awaiting action',
    };
  }

  return effectivenessReadForStamp(caseItem, variableId, signalRole, actionAt, state);
}

export function effectivenessFor(
  caseItem: Case,
  state: SessionState,
  advisory: AdvisorySessionState,
  diagnosis: Diagnosis,
  actions: readonly RecommendedAction[],
): EffectivenessRead {
  const { signalRole, variableId } = watchedSignalForCase(caseItem, diagnosis);

  if (!variableId || !signalRole) {
    return {
      caseId: caseItem.id,
      signalRole: null,
      variableId: null,
      label: '—',
      state: 'unobservable',
      sentence: 'No watched signal for this case',
    };
  }

  const variable = VARIABLE_BY_ID.get(variableId);
  const reading = variable ? readingByIdLookup(variable.readingId) : undefined;
  const label = reading?.label ?? variableId;

  const actionAt = actionStampForCase(caseItem, actions, state, advisory);
  if (!actionAt) {
    return {
      caseId: caseItem.id,
      signalRole,
      variableId,
      label,
      state: 'awaiting-action',
      sentence: `${label} — awaiting action`,
    };
  }

  return effectivenessReadForStamp(caseItem, variableId, signalRole, actionAt, state);
}
